import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { App, Button, Modal, Popconfirm, Radio, Space, Tree, Upload } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MenuItemForm from './MenuItemForm';
import buildTree from './buildTree';
import useMenuItemsApi from './useMenuItemsApi';

// Editor reutilizable de menu_items scopeado por tenantId. Recibe el id del
// tenant objetivo como prop; no asume wrapper propio (Card/Header) porque se
// embebe tanto en MenuItemsPage (liftlogic) como en TenantMenuItemsPage
// (/admin/tenants/:tenantId/menu-items).
const MenuItemsContent = ({ tenantId }) => {
  const { message } = App.useApp();
  const api = useMenuItemsApi();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('merge');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list(tenantId);
      setItems(data);
    } catch (e) {
      message.error('No se pudieron cargar los items');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, message]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.list(tenantId);
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) message.error('No se pudieron cargar los items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const treeData = useMemo(() => buildTree(items), [items]);

  const handleCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (raw) => {
    setEditing(raw);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.remove(id, tenantId);
      message.success('Eliminado');
      reload();
    } catch (e) {
      message.error(e?.response?.data?.message ?? 'No se pudo eliminar');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await api.update(editing.id, tenantId, values);
        message.success('Actualizado');
      } else {
        await api.create({ ...values, tenantId });
        message.success('Creado');
      }
      setFormOpen(false);
      reload();
    } catch (e) {
      message.error(e?.response?.data?.message ?? 'Error guardando');
    }
  };

  const handleDrop = async (info) => {
    const dragId = Number(info.dragNode.key);
    const dropId = Number(info.node.key);
    const dropToGap = info.dropToGap;

    const flatten = (nodes, parentId) => {
      const out = [];
      nodes.forEach((n, idx) => {
        out.push({ id: Number(n.key), parentId, order: idx });
        if (n.children?.length) out.push(...flatten(n.children, Number(n.key)));
      });
      return out;
    };

    let dragged = null;
    const removeById = (nodes) => {
      for (let i = 0; i < nodes.length; i++) {
        if (Number(nodes[i].key) === dragId) {
          dragged = nodes[i];
          nodes.splice(i, 1);
          return true;
        }
        if (nodes[i].children && removeById(nodes[i].children)) return true;
      }
      return false;
    };

    const clone = JSON.parse(JSON.stringify(treeData));
    removeById(clone);
    if (!dragged) return;

    const insert = (nodes) => {
      for (let i = 0; i < nodes.length; i++) {
        if (Number(nodes[i].key) === dropId) {
          if (!dropToGap) {
            nodes[i].children = nodes[i].children ?? [];
            nodes[i].children.unshift(dragged);
          } else {
            const offset = info.dropPosition > i ? 1 : 0;
            nodes.splice(i + offset, 0, dragged);
          }
          return true;
        }
        if (nodes[i].children && insert(nodes[i].children)) return true;
      }
      return false;
    };
    if (!insert(clone)) {
      clone.push(dragged);
    }

    const updates = flatten(clone, null);
    try {
      await api.reorder(tenantId, updates);
      message.success('Reordenado');
      reload();
    } catch (e) {
      message.error(e?.response?.data?.message ?? 'No se pudo reordenar');
      reload();
    }
  };

  const handleExport = async () => {
    try {
      await api.exportXlsx(tenantId);
    } catch (e) {
      message.error('No se pudo exportar');
    }
  };

  const handleImport = async (file) => {
    try {
      const result = await api.importXlsx(tenantId, file, importMode);
      message.success(
        `Import OK: ${result.inserted} creados, ${result.updated} actualizados, ${result.deleted} eliminados`
      );
      setImportOpen(false);
      reload();
    } catch (e) {
      message.error(e?.response?.data?.message ?? 'Import falló');
    }
    return false;
  };

  const titleRender = (node) => (
    <Space>
      <span>{node.title}</span>
      <Button
        size="small"
        type="text"
        icon={<EditOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(node.raw);
        }}
      />
      <Popconfirm
        title={`¿Eliminar "${node.raw.label}" y sus hijos?`}
        onConfirm={(e) => {
          e?.stopPropagation();
          handleDelete(node.raw.id);
        }}
        okText="Sí"
        cancelText="No"
      >
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Popconfirm>
    </Space>
  );

  return (
    <>
      <Space className="mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Agregar
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExport}>
          Exportar
        </Button>
        <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
          Importar
        </Button>
      </Space>
      {loading ? (
        <div>Cargando…</div>
      ) : treeData.length === 0 ? (
        <div>Este tenant no tiene menu_items todavía.</div>
      ) : (
        <Tree
          draggable
          blockNode
          defaultExpandAll
          treeData={treeData}
          onDrop={handleDrop}
          titleRender={titleRender}
        />
      )}

      <Modal
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        footer={null}
        title={editing ? 'Editar menu item' : 'Agregar menu item'}
        destroyOnHidden
      >
        <MenuItemForm
          initialValues={editing}
          items={items}
          onSubmit={handleSubmit}
          onCancel={() => setFormOpen(false)}
        />
      </Modal>

      <Modal
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        footer={null}
        title="Importar desde Excel"
        destroyOnHidden
      >
        <Space direction="vertical" className="w-full">
          <Radio.Group value={importMode} onChange={(e) => setImportMode(e.target.value)}>
            <Radio value="merge">Merge — actualiza/agrega sin borrar ausentes</Radio>
            <Radio value="replace">Replace — borra todos los del tenant y reinserta</Radio>
          </Radio.Group>
          <Upload accept=".xlsx" beforeUpload={handleImport} maxCount={1} showUploadList={false}>
            <Button icon={<UploadOutlined />}>Seleccionar archivo xlsx</Button>
          </Upload>
        </Space>
      </Modal>
    </>
  );
};

export default MenuItemsContent;
